import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Info } from 'lucide-react';
import { getTimeBucketLabel } from '../../services/dashboardTimeBucketService';
import ChartCard, { ChartLegend, ChartTooltip } from './ChartCard';
import {
  ANIMATION,
  MOBILE_MQ,
  axisProps,
  gridProps,
  seriesColor,
  toCount,
  useChartAnimation,
  useMediaQuery,
} from './chartTheme';

const COMPACT_MQ = '(max-width: 1200px)';

const Hint = styled.p`
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  margin: 0 0 1rem;
  padding: 0.6rem 0.75rem;
  border-radius: var(--border-radius-sm);
  background: var(--color-bg-secondary);
  color: var(--color-text-secondary);
  font-size: 0.8125rem;
  line-height: 1.45;

  svg {
    flex-shrink: 0;
    margin-top: 2px;
    color: var(--color-text-muted);
  }
`;

const ChartScroll = styled.div`
  width: 100%;
  min-width: 0;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
`;

const ChartPlot = styled.div`
  width: 100%;
  min-width: ${({ $minWidth }) => ($minWidth ? `${$minWidth}px` : '0')};
`;

const CHART_HEIGHT = 360;
const CHART_HEIGHT_COMPACT = 320;
const CHART_HEIGHT_MOBILE = 280;
const PERIOD_SLOT_PX = 64;

const formatPeriodLabel = (period, bucket) => {
  if (!period) return '';
  if (bucket === 'QUARTER') {
    const match = String(period).match(/^(\d{4})-Q(\d)$/);
    if (match) return `T${match[2]} ${match[1]}`;
    return period;
  }
  if (bucket === 'WEEK') {
    const match = String(period).match(/^(\d{4})-W(\d{2})$/);
    if (match) return `Sem ${match[2]}/${match[1]}`;
    return period;
  }
  const safe = String(period).length >= 7 ? `${String(period).slice(0, 7)}-01` : `${period}-01`;
  try {
    const raw = format(parseISO(safe), 'LLL/yy', { locale: ptBR });
    return raw.charAt(0).toUpperCase() + raw.slice(1);
  } catch {
    return period;
  }
};

const ResponsiblePerformanceOverTimeChart = ({
  content = [],
  seriesOrder = [],
  loading = false,
  error = false,
  onRetry = null,
  periodLabel = null,
  timeBucket = 'MONTH',
  showTopResponsiblesHint = false,
}) => {
  const isMobile = useMediaQuery(MOBILE_MQ);
  const isCompact = useMediaQuery(COMPACT_MQ) || isMobile;
  const animate = useChartAnimation();
  const [activeKey, setActiveKey] = useState(null);

  const { chartData, total } = useMemo(() => {
    const rows = Array.isArray(content) ? content : [];
    const byPeriod = new Map();
    let sum = 0;
    for (const row of rows) {
      const period = row?.period ?? '';
      const name = row?.responsibleUserName != null ? String(row.responsibleUserName) : '';
      const c = toCount(row?.count);
      if (!period || !name) continue;
      if (!byPeriod.has(period)) {
        byPeriod.set(period, {
          period,
          periodLabel: formatPeriodLabel(period, timeBucket),
        });
      }
      byPeriod.get(period)[name] = c;
      sum += c;
    }

    return {
      chartData: [...byPeriod.values()].sort((a, b) =>
        String(a.period).localeCompare(String(b.period))
      ),
      total: sum,
    };
  }, [content, timeBucket]);

  const orderedNames = useMemo(() => {
    const order = Array.isArray(seriesOrder) ? seriesOrder : [];
    const seen = new Set();
    const out = [];
    for (const name of order) {
      const key = String(name);
      if (key && !seen.has(key)) {
        seen.add(key);
        out.push(key);
      }
    }
    for (const row of chartData) {
      Object.keys(row).forEach((key) => {
        if (key === 'period' || key === 'periodLabel') return;
        if (!seen.has(key)) {
          seen.add(key);
          out.push(key);
        }
      });
    }
    return out;
  }, [seriesOrder, chartData]);

  const legendItems = orderedNames.map((name, idx) => ({
    key: name,
    label: name,
    color: seriesColor(idx),
  }));

  const chartHeight = isMobile
    ? CHART_HEIGHT_MOBILE
    : isCompact
      ? CHART_HEIGHT_COMPACT
      : CHART_HEIGHT;

  const minPlotWidth = Math.max(
    isMobile ? 320 : 0,
    chartData.length * (isMobile ? 52 : PERIOD_SLOT_PX)
  );

  const showDots = chartData.length <= 2;

  return (
    <ChartCard
      title="Evolução de resoluções por responsável"
      description={[
        periodLabel ? `Período: ${periodLabel}` : null,
        `Agrupamento: ${getTimeBucketLabel(timeBucket)}`,
      ]}
      stat={total}
      statLabel="resolvidos/fechados"
      loading={loading}
      error={error}
      onRetry={onRetry}
      isEmpty={chartData.length === 0}
      emptyTitle="Nenhuma resolução no período"
      emptyDescription="Tente ampliar o período ou remover filtros de responsável e tipo de demanda."
      skeleton="columns"
      skeletonHeight={chartHeight}
    >
      {showTopResponsiblesHint ? (
        <Hint>
          <Info size={14} strokeWidth={2} aria-hidden />
          <span>
            Exibindo os 8 responsáveis com mais resoluções no período. Use o filtro de responsável
            para focar em pessoas específicas.
          </span>
        </Hint>
      ) : null}
      <ChartLegend
        items={legendItems}
        variant="line"
        activeKey={activeKey}
        onActiveChange={setActiveKey}
        label="Responsáveis"
      />
      <ChartScroll>
        <ChartPlot $minWidth={minPlotWidth}>
          <ResponsiveContainer width="100%" height={chartHeight}>
            <LineChart data={chartData} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
              <CartesianGrid {...gridProps} vertical={false} />
              <XAxis
                dataKey="periodLabel"
                {...axisProps}
                interval={isCompact ? 'preserveStartEnd' : 0}
                minTickGap={isCompact ? 12 : 0}
                tickMargin={10}
              />
              <YAxis allowDecimals={false} width={40} {...axisProps} />
              <Tooltip
                cursor={{ stroke: 'var(--color-border-secondary)', strokeWidth: 1 }}
                wrapperStyle={{ zIndex: 2 }}
                content={<ChartTooltip showTotal />}
              />
              {orderedNames.map((name, idx) => (
                <Line
                  key={name}
                  type="monotone"
                  dataKey={name}
                  name={name}
                  stroke={seriesColor(idx)}
                  strokeWidth={activeKey === name ? 2.5 : 2}
                  strokeOpacity={activeKey && activeKey !== name ? 0.15 : 1}
                  dot={showDots ? { r: 3, strokeWidth: 0, fill: seriesColor(idx) } : false}
                  activeDot={{ r: 4, strokeWidth: 2, stroke: 'var(--color-bg-primary)' }}
                  connectNulls
                  isAnimationActive={animate}
                  {...ANIMATION}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </ChartPlot>
      </ChartScroll>
    </ChartCard>
  );
};

ResponsiblePerformanceOverTimeChart.propTypes = {
  content: PropTypes.arrayOf(
    PropTypes.shape({
      period: PropTypes.string,
      responsibleUserName: PropTypes.string,
      count: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    })
  ),
  seriesOrder: PropTypes.arrayOf(PropTypes.string),
  loading: PropTypes.bool,
  error: PropTypes.bool,
  onRetry: PropTypes.func,
  periodLabel: PropTypes.string,
  timeBucket: PropTypes.string,
  showTopResponsiblesHint: PropTypes.bool,
};

export default ResponsiblePerformanceOverTimeChart;
